import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Eye, BookOpen, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function HomePage({ stats }) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await axios.get(`${API}/books`);
        setBooks(response.data);
      } catch (error) {
        console.error('Failed to fetch books:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const featuredBooks = books.filter(book => book.featured);
  const allBooks = books;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F4F8] via-[#F5F1E8] to-[#D8F3F0]">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-6xl opacity-10 animate-float">🌴</div>
        <div className="absolute top-40 right-20 text-8xl opacity-10 animate-float" style={{ animationDelay: '1s' }}>🏝️</div>
        <div className="absolute bottom-20 left-1/4 text-7xl opacity-10 animate-float" style={{ animationDelay: '2s' }}>🌊</div>
        <div className="absolute top-1/2 right-10 text-6xl opacity-10 animate-float" style={{ animationDelay: '3s' }}>🥥</div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-md border-b border-[#0CAAA6]/20 sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <motion.div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              data-testid="brand-logo"
            >
              <span className="text-4xl animate-wave">🏝️</span>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#1A4D5C]">
                  Ebook Island
                </h1>
                <p className="text-sm text-[#7A8286]">from Ky4Kings</p>
              </div>
            </motion.div>

            <div className="flex items-center gap-4">
              {/* Stats Counter */}
              <div className="hidden sm:flex items-center gap-4 px-4 py-2 bg-white/60 rounded-full border border-[#0CAAA6]/30">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#0CAAA6]" />
                  <span className="text-sm font-medium text-[#1A4D5C]" data-testid="platform-visits">
                    {stats.platform_visits.toLocaleString()}
                  </span>
                  <span className="text-xs text-[#7A8286]">visits</span>
                </div>
                <div className="w-px h-4 bg-[#0CAAA6]/30"></div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#FF6B6B]" />
                  <span className="text-sm font-medium text-[#1A4D5C]" data-testid="total-reads">
                    {stats.total_reads.toLocaleString()}
                  </span>
                  <span className="text-xs text-[#7A8286]">reads</span>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="border-[#0CAAA6] text-[#0CAAA6] hover:bg-[#0CAAA6] hover:text-white"
                data-testid="admin-button"
              >
                Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A4D5C] mb-4">
            Welcome to your digital
            <br />
            <span className="text-[#0CAAA6]">reading paradise</span>
          </h2>
          <p className="text-lg text-[#7A8286] max-w-2xl mx-auto">
            Discover handcrafted e-books in a distraction-free oasis.
            Dive into stories that transport you to distant shores.
          </p>
        </motion.div>

        {/* Featured Books */}
        {featuredBooks.length > 0 && (
          <section className="mb-16">
            <h3 className="text-2xl font-bold text-[#1A4D5C] mb-6">Featured Collection</h3>
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {featuredBooks.map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex-shrink-0"
                >
                  <Card 
                    className="w-64 cursor-pointer hover:shadow-2xl transition-shadow border-[#0CAAA6]/20 overflow-hidden group"
                    onClick={() => navigate(`/book/${book.id}`)}
                    data-testid={`featured-book-${book.id}`}
                  >
                    <div className="relative h-96 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${book.cover_url}`}
                        alt={book.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#1A4D5C] mb-1 truncate">{book.title}</h4>
                      <p className="text-sm text-[#7A8286] mb-2">by {book.author}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm text-[#7A8286]">
                          <Eye className="w-4 h-4" />
                          <span>{book.read_count}</span>
                        </div>
                        {book.price && (
                          <span className="text-sm font-medium text-[#0CAAA6]">${book.price}</span>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* All Books Grid */}
        <section>
          <h3 className="text-2xl font-bold text-[#1A4D5C] mb-6">All E-Books</h3>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0CAAA6]"></div>
            </div>
          ) : books.length === 0 ? (
            <div className="text-center py-20">
              <span className="text-6xl mb-4 block">🏝️</span>
              <p className="text-xl text-[#7A8286] mb-2">The island is quiet today...</p>
              <p className="text-sm text-[#7A8286]">New books coming soon 🌴</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allBooks.map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="cursor-pointer hover:shadow-lg transition-all border-[#0CAAA6]/20 overflow-hidden group h-full"
                    onClick={() => navigate(`/book/${book.id}`)}
                    data-testid={`book-card-${book.id}`}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${book.cover_url}`}
                        alt={book.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {book.price && (
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <Lock className="w-3 h-3 inline mr-1 text-[#0CAAA6]" />
                          <span className="text-sm font-medium text-[#0CAAA6]">${book.price}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-[#1A4D5C] mb-1 line-clamp-1">{book.title}</h4>
                      <p className="text-sm text-[#7A8286] mb-2">by {book.author}</p>
                      <p className="text-sm text-[#7A8286] mb-3 line-clamp-2">{book.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm text-[#7A8286]">
                          <Eye className="w-4 h-4" />
                          <span>{book.read_count}</span>
                        </div>
                        <span className="text-xs text-[#7A8286]">{book.page_count} pages</span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-white/60 backdrop-blur-sm border-t border-[#0CAAA6]/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-sm text-[#7A8286]">
            © 2025 Ebook Island by Ky4Kings. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}